/**
 * TiktokPanel — pestaña TikTok de Redes Orgánicas.
 *
 * Datos: /api/tiktok/organic/overview (Accounts API oficial de TikTok Business):
 * perfil, insights agregados del periodo y últimos videos. TikTok acota el
 * reporte a 30 días por request (backend: tiktok_client.REPORT_MAX_DAYS). En los
 * tenants demo / App Review el backend sirve el fixture tiktok_organic.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  FaTiktok, FaUsers, FaEye, FaHeart, FaComment, FaShare, FaChartBar, FaTrophy,
  FaPlay, FaExclamationTriangle, FaUserFriends, FaRegThumbsUp, FaPercent,
} from 'react-icons/fa';
import { integrationsApi } from '../../services/integrationsService';
import {
  numFmt, compact, pct2, filterByRange, insightWindowLabel, Kpi,
} from '../../components/SocialApi/socialApiShared';
import {
  PeriodSummary, PostsSection, PostCard, PostsTable, PostModal, CommentsBlock,
  StackedByDateChart, TopChart, chartBox, useDayPick, engagementRate,
} from './organicShared';

const TT_INSIGHTS_MAX_DAYS = 30;
const inter = (v) => (v.likes || 0) + (v.comments || 0) + (v.shares || 0);

export default function TiktokPanel({ data, range, onRangeChange, username, t }) {
  const [modalVideo, setModalVideo] = useState(null);

  const acc = data?.account || {};
  const videos = useMemo(
    () => filterByRange(data?.videos || [], range).map((v) => ({ ...v, inter: inter(v) })),
    [data, range]);
  const dayPick = useDayPick(videos, setModalVideo);
  const best = useMemo(() => videos.reduce((b, v) => (!b || (v.views || 0) > (b.views || 0) ? v : b), null), [videos]);
  const totalInter = videos.reduce((a, v) => a + v.inter, 0);
  const totalViews = videos.reduce((a, v) => a + (v.views || 0), 0);
  const avgInter = videos.length ? Math.round(totalInter / videos.length) : 0;
  const insightSub = insightWindowLabel(range, TT_INSIGHTS_MAX_DAYS, t);
  // Interacciones de la CUENTA en el periodo (no solo de los videos listados).
  const accInter = [acc.likes, acc.comments, acc.shares].every((x) => x == null)
    ? null : (acc.likes || 0) + (acc.comments || 0) + (acc.shares || 0);

  const sorts = useMemo(() => [
    { id: 'recent', label: t('organic.sortRecent'), fn: (a, b) => new Date(b.timestamp) - new Date(a.timestamp) },
    { id: 'views', label: t('organic.sortViews'), fn: (a, b) => (b.views || 0) - (a.views || 0) },
    { id: 'top', label: t('organic.sortTop'), fn: (a, b) => b.inter - a.inter },
  ], [t]);

  const videoMetrics = (v) => [
    { icon: <FaHeart />, value: numFmt.format(v.likes || 0), title: t('organic.likes') },
    { icon: <FaComment />, value: numFmt.format(v.comments || 0), title: t('organic.comments') },
    { icon: <FaShare />, value: numFmt.format(v.shares || 0), title: t('organic.shares') },
  ];

  const fetchComments = useCallback(async () => {
    const res = await integrationsApi.tiktokOrganicComments(username, modalVideo?.id);
    return res.comments || [];
  }, [username, modalVideo]);

  return (
    <>
      <section className="soc-kpis">
        <Kpi icon={<FaUsers />} accent="#FE2C55" label={t('organic.followers')} value={compact(acc.followers)}
             sub={acc.videos_count != null ? t('organic.videosCount', { n: numFmt.format(acc.videos_count) }) : null}
             hint={t('organic.followersHint')} />
        <Kpi icon={<FaEye />} accent="#25F4EE" label={t('organic.videoViews')} value={compact(acc.video_views)} sub={insightSub} hint={t('organic.videoViewsHint')} />
        <Kpi icon={<FaUserFriends />} accent="#F68D2E" label={t('organic.profileVisits')} value={compact(acc.profile_views)} sub={insightSub} hint={t('organic.profileVisitsHint')} />
        <Kpi icon={<FaRegThumbsUp />} accent="#8b5cf6" label={t('organic.interactions')} value={compact(accInter)}
             sub={accInter != null ? `${compact(acc.likes)} ${t('organic.likes').toLowerCase()} · ${compact(acc.comments)} ${t('organic.comments').toLowerCase()} · ${compact(acc.shares)} ${t('organic.shares').toLowerCase()}` : insightSub}
             hint={t('organic.interactionsHintTt')} />
        <Kpi icon={<FaPercent />} accent="#10b981" label={t('organic.engagement')} value={pct2(engagementRate(videos, acc.followers))}
             sub={videos.length ? t('organic.engagementSubVideos', { n: numFmt.format(videos.length) }) : t('organic.engagementNoneVideos')}
             hint={t('organic.engagementHint')} />
      </section>

      {acc.videos_error && (
        <div className="soc-warn" title={acc.videos_error}><FaExclamationTriangle /> {t('organic.partialData')}</div>
      )}

      <PeriodSummary
        t={t}
        stats={[
          { label: t('organic.statVideos'), value: numFmt.format(videos.length) },
          { label: t('organic.statVideoViews'), value: compact(totalViews) },
          { label: t('organic.statAvgVideo'), value: numFmt.format(avgInter) },
          { label: t('organic.statInterPerView'), value: totalViews ? pct2(totalInter / totalViews) : '—', hint: t('organic.statEngagementTtHint') },
        ]}
        best={best && (best.views || 0) > 0 ? best : null}
        bestTitle={t('organic.bestVideo')}
        bestMetric={t('organic.nViews', { n: numFmt.format(best?.views || 0) })}
        placeholderIcon={<FaTiktok />}
        onOpen={setModalVideo}
      />

      {videos.length > 0 && (
        <div className="soc-grid">
          <section className="soc-chart-card col-8">
            <div className="soc-chart-head">
              <span className="soc-chart-title"><FaChartBar /> {t('organic.chartInterByVideo')}</span>
              <span className="soc-chart-tag">{t('organic.byDate')}</span>
            </div>
            {chartBox(<StackedByDateChart items={videos} captionOf={(v) => v.caption} onPick={dayPick.pick} series={[
              { key: 'likes', label: t('organic.likes'), color: '#FE2C55' },
              { key: 'comments', label: t('organic.comments'), color: '#25F4EE' },
              { key: 'shares', label: t('organic.shares'), color: '#073C5C' },
            ]} />)}
          </section>

          <section className="soc-chart-card col-4">
            <div className="soc-chart-head"><span className="soc-chart-title"><FaTrophy /> {t('organic.chartTopVideos')}</span></div>
            {chartBox(<TopChart items={videos} t={t} valueOf={(v) => v.views || 0} captionOf={(v) => v.caption} color="#FE2C55" onOpen={setModalVideo}
              tooltipLabel={(v) => `${t('organic.colViews')} ${numFmt.format(v.views || 0)} · ${t('organic.colInter')} ${numFmt.format(v.inter)}`} />, true)}
          </section>
        </div>
      )}

      <PostsSection
        t={t}
        title={t('organic.videosTitle')}
        icon={<FaTiktok />}
        items={dayPick.items}
        day={dayPick.day}
        onClearDay={dayPick.clear}
        sectionRef={dayPick.sectionRef}
        sorts={sorts}
        emptyText={t('organic.emptyVideos')}
        range={range}
        onRangeChange={onRangeChange}
        renderGrid={(list) => (
          <div className="soc-posts">
            {list.map((v) => (
              <PostCard key={v.id} post={v} t={t} onOpen={setModalVideo} badge={<><FaPlay /> {compact(v.views)}</>}
                metrics={videoMetrics(v).slice(0, 2)} placeholderIcon={<FaTiktok />} />
            ))}
          </div>
        )}
        renderTable={(list) => (
          <PostsTable posts={list} t={t} onOpen={setModalVideo} firstLabel={t('organic.colVideo')} columns={[
            { label: t('organic.colViews'), render: (v) => numFmt.format(v.views || 0), strong: true },
            { label: t('organic.likes'), render: (v) => numFmt.format(v.likes || 0) },
            { label: t('organic.comments'), render: (v) => numFmt.format(v.comments || 0) },
            { label: t('organic.shares'), render: (v) => numFmt.format(v.shares || 0) },
          ]} />
        )}
      />

      {modalVideo && (
        <PostModal t={t} post={modalVideo} onClose={() => setModalVideo(null)} network="TikTok"
          badge={<><FaPlay /> {compact(modalVideo.views)}</>} metrics={videoMetrics(modalVideo)} placeholderIcon={<FaTiktok />}
          footer={<CommentsBlock key={modalVideo.id} t={t} fetchComments={fetchComments} />} />
      )}
    </>
  );
}
